import React, { useState } from 'react';
import { Send, CheckCircle } from 'lucide-react';
import FadeIn from '../components/FadeIn';
import { useSiteData } from '../contexts/SiteDataContext';
import { artistInfo as artist } from '../data/site';

const ContactInfo = ({ artistInfo }) => (
  <div className="lg:col-span-4">
    <FadeIn>
      <h2 className="text-[17px] font-light text-[#1a1a1a] mb-5">Say hello!</h2>
      <p className="text-[13px] leading-[1.75] text-[#888]">
        For any inquiries regarding the purchase of original artwork, commissioning a custom piece, or general questions about my work, email{' '}
        <a href={`mailto:${artistInfo.email}`} className="text-[#555] hover:text-[#1a1a1a] transition-colors border-b border-[#ddd]">{artistInfo.email}</a>{' '}
        or fill in the form.
      </p>
      <div className="mt-8 space-y-4">
        <div>
          <p className="text-[11px] text-[#bbb] mb-1">Email</p>
          <a href={`mailto:${artistInfo.email}`} className="text-[13px] text-[#666] hover:text-[#1a1a1a] transition-colors">{artistInfo.email}</a>
        </div>
        <div>
          <p className="text-[11px] text-[#bbb] mb-1">Location</p>
          <p className="text-[13px] text-[#666]">Smithtown, New York</p>
        </div>
      </div>
      <p className="text-[13px] text-[#888] mt-8">
        Thank you,<br /><span className="text-[#555]">Sebastian McLaughlin</span>
      </p>
    </FadeIn>
  </div>
);

const ContactForm = () => {
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', phone: '', message: '' });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    const lines = [
      `Name: ${formData.firstName} ${formData.lastName}`.trim(),
      `Email: ${formData.email}`,
      `Phone: ${formData.phone || '—'}`,
      '',
      formData.message,
    ];
    const subject = `Website message from ${formData.firstName} ${formData.lastName}`.trim();
    const mailto = `mailto:${artist.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines.join('\n'))}`;
    window.location.href = mailto;
    setIsSubmitted(true);
  };

  if (isSubmitted) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="contact-success">
        <CheckCircle size={36} className="text-green-500/70 mb-5" />
        <h3 className="text-lg font-light text-[#1a1a1a]">Your email is ready to send</h3>
        <p className="text-[13px] text-[#999] mt-3 max-w-sm">Your email app should have opened with your message. Just press send. If nothing opened, email <a href={`mailto:${artist.email}`} className="border-b border-[#ddd] hover:text-[#1a1a1a]">{artist.email}</a> directly.</p>
        <button onClick={() => setIsSubmitted(false)} className="mt-6 text-[12px] text-[#999] hover:text-[#1a1a1a] transition-colors border-b border-[#ddd]">
          Edit the message again
        </button>
      </div>
    );
  }

  const inputClass = 'w-full bg-transparent border-b border-[#e5e5e5] focus:border-[#999] text-[#1a1a1a] text-[14px] py-2.5 outline-none transition-colors duration-300 placeholder:text-[#ddd]';

  return (
    <form onSubmit={handleSubmit} className="space-y-7" data-testid="contact-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
        <div>
          <label className="text-[11px] text-[#bbb] block mb-2">First name <span className="text-[#999]">*</span></label>
          <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required className={inputClass} placeholder="Your first name" data-testid="contact-firstName" />
        </div>
        <div>
          <label className="text-[11px] text-[#bbb] block mb-2">Last name</label>
          <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} className={inputClass} placeholder="Your last name" data-testid="contact-lastName" />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
        <div>
          <label className="text-[11px] text-[#bbb] block mb-2">Email address <span className="text-[#999]">*</span></label>
          <input type="email" name="email" value={formData.email} onChange={handleChange} required className={inputClass} placeholder="your@email.com" data-testid="contact-email" />
        </div>
        <div>
          <label className="text-[11px] text-[#bbb] block mb-2">Phone</label>
          <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className={inputClass} placeholder="Your phone number" data-testid="contact-phone" />
        </div>
      </div>
      <div>
        <label className="text-[11px] text-[#bbb] block mb-2">Message <span className="text-[#999]">*</span></label>
        <textarea name="message" value={formData.message} onChange={handleChange} required rows={5} className={`${inputClass} resize-none`} placeholder="Tell me about your inquiry..." data-testid="contact-message" />
      </div>
      <div className="pt-2">
        <button type="submit" data-testid="contact-submit" className="inline-flex items-center gap-2.5 text-[12px] bg-[#1a1a1a] hover:bg-[#333] text-white px-8 py-3.5 transition-colors duration-300 disabled:opacity-50">
          <Send size={13} /> Send message
        </button>
      </div>
    </form>
  );
};

const Contact = () => {
  const { artistInfo, isLoading } = useSiteData();
  if (isLoading) return <div className="min-h-[60vh] flex items-center justify-center text-[13px] text-[#aaa]">Loading…</div>;

  return (
    <div data-testid="contact-page">
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-12 md:pt-20 pb-10">
        <FadeIn>
          <h1 className="text-xl md:text-3xl font-light text-[#1a1a1a] tracking-[0.01em]">Contact</h1>
          <div className="w-10 h-px bg-[#ddd] mt-5" />
        </FadeIn>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
          <ContactInfo artistInfo={artistInfo} />
          <div className="lg:col-span-8">
            <FadeIn delay={150}><ContactForm /></FadeIn>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
